import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { validateAdminContent, checkContentUpdateRateLimit } from '@/lib/contentSecurity';
import { useAuth } from '@/contexts/AuthContext';

interface ContentEditorProps {
  componentType: 'WhatWeDo' | 'WhyBuyFromUs';
  onSave: () => void;
}

const ContentEditor = ({ componentType, onSave }: ContentEditorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { user } = useAuth();

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_contents')
        .select('*')
        .eq('page_name', 'homepage')
        .eq('section_name', componentType)
        .order('order_index');

      if (error) throw error;

      if (data && data.length > 0) {
        // Parse existing content
        const parsedItems = data.map(item => JSON.parse(item.content));
        setItems(parsedItems);
      } else {
        // Set default content based on component type
        setItems(getDefaultContent(componentType));
      }
    } catch (error) {
      console.error('Error loading content:', error);
      setItems(getDefaultContent(componentType));
    }
  };

  const getDefaultContent = (type: string) => {
    if (type === 'WhatWeDo') {
      return [
        { title: "1. רוכשים עודפי פרימיום ישירות מיבואנים מובילים", icon: "ShoppingCart" },
        { title: "2. בודקים ומקטלגים כל פריט לפני העלאתו לאתר", icon: "Search" },
        { title: "3. בזכות הסכמים בלעדיים תקבלו הנחות של עשרות אחוזים", icon: "Percent" },
        { title: "4. כל החיסכון עובר אליכם, בלי אותיות קטנות", icon: "Heart" }
      ];
    } else {
      return [
        { title: "מחירים משתלמים", description: "בזכות חוזים בלעדיים עם ספקים ויבואנים, תוכלו ליהנות מהנחות משמעותיות ולרכוש מוצרים איכותיים בעלות נמוכה.", icon: "DollarSign" },
        { title: "איכות ובטיחות", description: "כל מוצר עובר בדיקה, מדידה וסימון כדי להבטיח איכות ועמידה בתקנים.", icon: "Shield" },
        { title: "חוויית קנייה נוחה", description: "הזמנה קלה, תשלום מאובטח ומשלוח או איסוף לבחירתכם.", icon: "ShoppingCart" },
        { title: "תרומה לסביבה", description: "קנייה של חומרי‑בנייה עודפים מצמצמת את ייצור המוצרים החדשים ואת זיהום הסביבה, שומרת על משאבים ומונעת הטמנת פסולת.", icon: "Leaf" },
        { title: "שקיפות מלאה", description: "מציינים את הכמות הכוללת, את הכמות המינימלית להזמנה ואת היתרה בזמן אמת.", icon: "Eye" },
        { title: "מציאת מוצרים", description: "לא מצאתם את המוצר שאתם צריכים? שלחו לנו מפרט וכמות - ואנחנו נאתר עבורכם פתרון מהיר ומשתלם.", icon: "Search" }
      ];
    }
  };

  const handleSaveContent = async () => {
    setLoading(true);
    try {
      // Check rate limiting
      if (!user?.id || !checkContentUpdateRateLimit(user.id)) {
        toast.error('יותר מדי עדכונים במהירות. אנא המתן דקה לפני ניסיון נוסף.');
        return;
      }

      // Validate all items
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const content = JSON.stringify(item);
        
        const validation = validateAdminContent(content, 'json');
        if (!validation.isValid) {
          toast.error(`פריט ${i + 1} לא תקין: ${validation.message}`);
          return;
        }

        // Validate individual fields
        if (!item.title || item.title.trim() === '') {
          toast.error(`פריט ${i + 1} חסר כותרת`);
          return;
        }

        if (componentType === 'WhyBuyFromUs' && (!item.description || item.description.trim() === '')) {
          toast.error(`פריט ${i + 1} חסר תיאור`);
          return;
        }
      }

      // Delete existing content for this section
      await supabase
        .from('page_contents')
        .delete()
        .eq('page_name', 'homepage')
        .eq('section_name', componentType);

      // Insert updated content
      for (let i = 0; i < items.length; i++) {
        await supabase
          .from('page_contents')
          .insert({
            page_name: 'homepage',
            section_name: componentType,
            content_type: 'json',
            content: JSON.stringify(items[i]),
            order_index: i
          });
      }

      toast.success('התוכן עודכן בהצלחה');
      onSave();
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('שגיאה בשמירת התוכן');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index: number, field: string, value: string) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const openEditor = () => {
    setIsOpen(true);
    loadContent();
  };

  return (
    <>
      <Button onClick={openEditor} variant="outline" size="sm">
        ערוך תוכן {componentType === 'WhatWeDo' ? 'מה אנחנו עושים' : 'למה לקנות אצלנו'}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              עריכת תוכן - {componentType === 'WhatWeDo' ? 'מה אנחנו עושים' : 'למה לקנות אצלנו'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {items.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor={`title-${index}`}>כותרת</Label>
                    <Input
                      id={`title-${index}`}
                      value={item.title}
                      onChange={(e) => updateItem(index, 'title', e.target.value)}
                      className="text-right"
                      dir="rtl"
                    />
                  </div>
                  
                  {componentType === 'WhyBuyFromUs' && (
                    <div>
                      <Label htmlFor={`description-${index}`}>תיאור</Label>
                      <Textarea
                        id={`description-${index}`}
                        value={item.description || ''}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="text-right"
                        dir="rtl"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                ביטול
              </Button>
              <Button onClick={handleSaveContent} disabled={loading}>
                {loading ? 'שומר...' : 'שמור שינויים'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentEditor;